import { TooltipButton } from '@/ui/TooltipButton';
// shell/Rail/Rail.tsx — full-height left rail: brand + collapse toggle / scrolling nav /
// pinned promo + account chip + footer. Nav items + badges come from the nav registry + hooks.
//
// Collapsed (icons-only) state: when useRailCollapsed().collapsed is true the grid column is 76px,
// so the rail hides the wordmark, nav labels, promo, and footer; nav items center their icon and
// show a dot instead of a numeric badge; the account chip renders avatar-only. The collapse toggle
// reflects state via aria-expanded + a dynamic label. Labels remain mounted for transitions.

import { NavLink, Link } from 'react-router-dom';
import styled from 'styled-components';
import { Icon } from '@/icons';
import { MetaRow } from '@/ui';
import { LogoTriad } from '@/icons/brand/LogoTriad';
import { useNotifications, useMessagesByType, useSession } from '@/hooks';
import { NAV_ITEMS, type NavBadge } from '../nav';
import { useRailCollapsed } from '../useRailCollapsed';
import { quietScrollbar } from '@/theme/scrollbar';
import { AccountChip } from './AccountChip';
import { softControlMotion } from '@/theme/interactions';
import { SIGNUP_CTA_BAR_HEIGHT } from '../SignupCtaBar';

const Aside = styled.aside<{ $guestBar: boolean }>`
  grid-column: 1;
  grid-row: 1 / span 2;
  min-height: 0;
  min-width: 0;
  /* Only the nav and animated labels clip; account popovers may cross the rail. */
  position: relative;
  z-index: 11;
  display: flex;
  flex-direction: column;
  gap: 12px;
  /* Fix-round item 14: the guest SignupCtaBar is position:fixed and spans the
     full viewport width, so it visually overlaps THIS rail's bottom-pinned
     AccountChip/footer on desktop/tablet. Reserve its height here so those
     stay fully visible/clickable above the bar, mirroring the same padding
     reservation ShellLayout's Center already makes for the feed. */
  padding: 0 0 ${({ $guestBar }) => ($guestBar ? 16 + SIGNUP_CTA_BAR_HEIGHT : 16)}px;
  border-right: 1px solid ${({ theme }) => theme.color.glassBorder};
  background: ${({ theme }) => theme.color.surfaceChrome};
`;

const Brand = styled.div<{ $collapsed: boolean }>`
  display: flex;
  align-items: center;
  gap: 9px;
  height: 60px;
  flex: none;
  padding: 0 16px;
  border-bottom: 1px solid ${({ theme }) => theme.color.glassBorder};
  overflow: hidden;
  ${({ $collapsed }) => $collapsed && 'gap: 0;'}
  transition: gap 260ms ease;
  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
  font-family: ${({ theme }) => theme.font.display};
  font-weight: 800;
  /* md (not lg): at 20px the wordmark didn't fit beside the 30px logo + 30px toggle in a
     240px rail and ejected/truncated. md keeps the full "TruXSocial" with the toggle. */
  font-size: ${({ theme }) => theme.fontSize.md};
  color: ${({ theme }) => theme.color.textPrimary};
`;

const BrandHome = styled(Link)<{ $collapsed: boolean }>`
  width: ${({ $collapsed }) => ($collapsed ? '0' : '30px')};
  height: 30px;
  flex: none;
  border-radius: ${({ theme }) => theme.radius.sm};
  display: grid;
  place-items: center;
  overflow: hidden;
  opacity: ${({ $collapsed }) => ($collapsed ? 0 : 1)};
  transition:
    width 260ms ease,
    opacity 140ms ease;
  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.color.brandFrom};
    outline-offset: 2px;
  }
`;

const Wordmark = styled.span<{ $collapsed: boolean }>`
  flex: 1;
  /* Reserve the toggle's space inside the rail: without min-width:0 the long wordmark
     overflowed and pushed the collapse Toggle past the rail edge, under the Topbar
     (z-index:10), leaving it invisible and unclickable. */
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: ${({ $collapsed }) => ($collapsed ? '0' : '160px')};
  opacity: ${({ $collapsed }) => ($collapsed ? 0 : 1)};
  transform: ${({ $collapsed }) => ($collapsed ? 'translateX(-6px)' : 'none')};
  transition:
    max-width 260ms ease,
    opacity 160ms ease,
    transform 260ms ease;
  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

const Toggle = styled(TooltipButton)<{ $collapsed: boolean }>`
  margin-left: auto;
  margin-right: ${({ $collapsed }) => ($collapsed ? 'auto' : '0')};
  display: grid;
  place-items: center;
  width: 36px;
  height: 36px;
  flex: 0 0 36px;
  border-radius: ${({ theme }) => theme.radius.sm};
  border: 1px solid ${({ theme }) => theme.color.glassBorder};
  background: transparent;
  color: ${({ theme }) => theme.color.textMuted};
  cursor: pointer;
  &:hover {
    color: ${({ theme }) => theme.color.textPrimary};
    background: ${({ theme }) => theme.color.hover};
  }
`;

const Nav = styled.nav`
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  ${quietScrollbar}
`;

const Item = styled(NavLink)<{ $collapsed: boolean }>`
  position: relative;
  display: flex;
  align-items: center;
  gap: ${({ $collapsed }) => ($collapsed ? '0' : '12px')};
  padding: 10px 12px;
  margin-inline: 8px;
  min-height: 44px;
  flex-shrink: 0;
  box-sizing: border-box;
  justify-content: flex-start;
  overflow: hidden;
  & > svg {
    flex-shrink: 0;
  }
  border-radius: ${({ theme }) => theme.radius.md};
  text-decoration: none;
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.color.textMuted};
  &:hover {
    color: ${({ theme }) => theme.color.textPrimary};
    text-decoration: none;
    background: ${({ theme }) => theme.color.surfaceRaised};
  }
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.color.focusRing};
    outline-offset: -3px;
  }
  &.active {
    background: ${({ theme }) =>
      theme.surfaceGradients.panel === 'none'
        ? theme.color.surfaceRaised
        : theme.surfaceGradients.panel};
    scale: var(--control-selected-scale, 1.015);
    color: ${({ theme }) => theme.color.textPrimary};
    font-weight: 600;
  }
  ${softControlMotion}
  transition:
    gap 260ms ease,
    background 140ms ease,
    color 140ms ease;
  @media (prefers-reduced-motion: reduce) {
    transition: none;
    &.active {
      scale: none;
    }
  }
`;

const Label = styled.span<{ $collapsed: boolean }>`
  flex: 1;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  max-width: ${({ $collapsed }) => ($collapsed ? '0' : '170px')};
  opacity: ${({ $collapsed }) => ($collapsed ? 0 : 1)};
  transform: ${({ $collapsed }) => ($collapsed ? 'translateX(-6px)' : 'none')};
  transition:
    max-width 260ms ease,
    opacity 160ms ease,
    transform 260ms ease;
  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

const Count = styled.span<{ $collapsed: boolean }>`
  font-size: ${({ theme }) => theme.fontSize.xs};
  font-variant-numeric: tabular-nums;
  max-width: ${({ $collapsed }) => ($collapsed ? '0' : '40px')};
  opacity: ${({ $collapsed }) => ($collapsed ? 0 : 1)};
  overflow: hidden;
  transition:
    opacity 140ms ease,
    max-width 260ms ease;
  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

const CollapsibleBlock = styled.div<{ $collapsed: boolean }>`
  display: grid;
  grid-template-rows: ${({ $collapsed }) => ($collapsed ? '0fr' : '1fr')};
  opacity: ${({ $collapsed }) => ($collapsed ? 0 : 1)};
  margin-inline: 14px;
  & > div {
    min-height: 0;
    overflow: hidden;
  }
  transition:
    grid-template-rows 260ms ease,
    opacity 160ms ease;
  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

const Dot = styled.span`
  position: absolute;
  top: 7px;
  right: 12px;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${({ theme }) => theme.color.accentMagenta};
`;

/* A notice bounded by two rules, not a tinted card. Every generic-template
   signal in the app was concentrated in this one 120px block: a CROWN as the
   premium metaphor, a SPARKLES glyph on the CTA (the most AI-associated icon of
   2026), and centred text inside an otherwise strictly left-aligned rail.
   Centring is for empty states and auth cards; a sidebar notice is neither. */
const Promo = styled.div`
  padding: 14px 2px;
  text-align: left;
  border-top: 1px solid ${({ theme }) => theme.color.glassBorder};
  border-bottom: 1px solid ${({ theme }) => theme.color.glassBorder};
  color: ${({ theme }) => theme.color.textMuted};
  font-size: ${({ theme }) => theme.fontSize.xs};
  line-height: 1.45;
`;

const PromoTitle = styled.b`
  display: block;
  margin: 0 0 4px;
  color: ${({ theme }) => theme.color.textPrimary};
  font-family: ${({ theme }) => theme.font.display};
  font-size: ${({ theme }) => theme.fontSize.sm};
  font-weight: 600;
`;

/* A real destination. This was a <Button> with no onClick and no href — it
   looked like the rail's one call to action and did nothing at all, for
   everyone, signed in or out. Styled as a link because it IS one. */
const PromoLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  margin-top: 8px;
  padding: 7px 12px;
  border-radius: ${({ theme }) => theme.radius.md};
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.xs};
  font-weight: 600;
  color: ${({ theme }) => theme.color.textPrimary};
  text-decoration: none;
  border: 1px solid ${({ theme }) => theme.color.hairSoft};
  &:hover {
    background: ${({ theme }) => theme.color.hover};
  }
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.color.focusRing};
    outline-offset: 2px;
  }
`;

/* Left-aligned like everything else in this rail, and dimmed ONCE. It used to
   be centred and dimmed twice (textMuted and then opacity 0.8), which is the
   "make it look subtle" reflex rather than a designed tertiary tier — and the
   compounded value dropped it below the contrast floor. textFaint IS that tier. */
const Footer = styled.div`
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.color.textFaint};
  text-align: left;
  line-height: 1.5;
`;

const AccountSlot = styled.div`
  margin-inline: 14px;
  min-width: 0;
`;

// Pure helper (NOT a hook — no `use` prefix) so it can be called inside .map().
function badgeCountFor(
  badge: NavBadge | undefined,
  notif: number,
  dm: number,
  group: number,
): number {
  if (badge === 'notifications') return notif;
  if (badge === 'dm') return dm;
  if (badge === 'group') return group;
  return 0;
}

export function Rail() {
  const { unreadCount } = useNotifications();
  const dmUnread = useMessagesByType('dm').conversations.reduce((s, c) => s + c.unread, 0);
  const groupUnread = useMessagesByType('group').conversations.reduce((s, c) => s + c.unread, 0);
  const collapsed = useRailCollapsed((s) => s.collapsed);
  const toggle = useRailCollapsed((s) => s.toggle);
  const { isAdmin, isAuthenticated } = useSession();
  // Fix-round item 21: a guest sees only the genuinely public destinations
  // (Home/Explore/Support — see nav.ts's authOnly flag) instead of a full
  // 13-item rail where ~11 links just bounce them to /signin on click.
  const items = NAV_ITEMS.filter(
    (i) => (!i.staffOnly || isAdmin) && (!i.authOnly || isAuthenticated),
  );

  return (
    <Aside aria-label="Primary" data-testid="rail" $guestBar={!isAuthenticated}>
      <Brand $collapsed={collapsed}>
        <BrandHome
          to="/"
          aria-label="TruXSocial home"
          $collapsed={collapsed}
          tabIndex={collapsed ? -1 : undefined}
          aria-hidden={collapsed || undefined}
        >
          <LogoTriad size={30} />
        </BrandHome>
        <Wordmark $collapsed={collapsed} aria-hidden={collapsed || undefined}>
          TruXSocial
        </Wordmark>
        <Toggle
          type="button"
          $collapsed={collapsed}
          aria-pressed={collapsed}
          aria-expanded={!collapsed}
          aria-controls="sidebar-navigation"
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          onClick={toggle}
        >
          <Icon name="panel-left" size={20} />
        </Toggle>
      </Brand>

      <Nav id="sidebar-navigation">
        {items.map((item) => {
          const count = badgeCountFor(item.badge, unreadCount, dmUnread, groupUnread);
          return (
            <Item
              key={item.path}
              to={item.path}
              end={item.path === '/'}
              $collapsed={collapsed}
              aria-label={collapsed ? item.label : undefined}
              title={collapsed ? item.label : undefined}
            >
              <Icon name={item.icon} size={19} />
              <Label $collapsed={collapsed} aria-hidden={collapsed || undefined}>
                {item.label}
              </Label>
              {count > 0 && (
                <Count $collapsed={collapsed} aria-hidden={collapsed || undefined}>
                  {count}
                </Count>
              )}
              {count > 0 && collapsed && <Dot aria-hidden />}
            </Item>
          );
        })}
      </Nav>

      {/* Guests are not pretend members (1.24.70 ruling, extended here): an
          "Upgrade Now" offered to someone with no account is an upgrade of
          nothing, and /premium bounces them to the sign-in form anyway.
          Supporting the platform, on the other hand, genuinely needs no
          account — /support is a public route. So the promo keeps its place
          and changes what it offers. */}
      <CollapsibleBlock
        $collapsed={collapsed}
        inert={collapsed}
        aria-hidden={collapsed || undefined}
      >
        <div>
          <Promo>
            <PromoTitle>Freedom is Priceless.</PromoTitle>
            {isAuthenticated ? 'Upgrade & support free speech.' : 'Support free speech here.'}
            <PromoLink to={isAuthenticated ? '/premium' : '/support'}>
              {isAuthenticated ? 'Upgrade Now' : 'Support TruXSocial'}
            </PromoLink>
          </Promo>
        </div>
      </CollapsibleBlock>

      <AccountSlot>
        <AccountChip collapsed={collapsed} />
      </AccountSlot>

      <CollapsibleBlock
        $collapsed={collapsed}
        inert={collapsed}
        aria-hidden={collapsed || undefined}
      >
        <div>
          <Footer>
            <MetaRow block>
              <span>© 2026 TruXSocial</span>
              <span>About</span>
              <span>Privacy</span>
              <span>Terms</span>
            </MetaRow>
          </Footer>
        </div>
      </CollapsibleBlock>
    </Aside>
  );
}
